// src/hooks/useInViewAnimation.ts
import { useEffect, useRef, useState } from 'react';

// Options for the IntersectionObserver
interface IntersectionOptions {
  threshold?: number | number[]; // Percentage of the target element which is visible
  rootMargin?: string; // Margin around the root (viewport)
  once?: boolean; // Only animate once
}

export const useInViewAnimation = (options?: IntersectionOptions) => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  const { threshold = 0.1, rootMargin = '0px', once = true } = options || {};

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (once) {
            observer.disconnect(); // Stop observing after it's in view
          }
        } else {
          // If 'once' is false, you might want to reset 'inView' when it leaves
          if (!once) {
            setInView(false);
          }
        }
      },
      {
        threshold,
        rootMargin,
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        // Ensure ref.current exists before disconnecting
        observer.disconnect();
      }
    };
  }, [threshold, rootMargin, once]); // Dependencies for useEffect

  return { ref, inView };
};
